import type { QuoteOption } from '@techgitter/contracts';

/**
 * Per-unit pricing helpers for product-type quote options.
 *
 * `QuoteOption.sellPrice`/`buyPriceExGst` are PER-UNIT (ex-GST). The
 * customer-facing line total for an option is `quantity × sellPrice`,
 * rounded to 2dp. Legacy options carry no `quantity` and are treated as
 * a single unit, so their line total equals `sellPrice` exactly.
 *
 * These are the single source of truth for "how much is this option's
 * line worth" — every total/preview/invoice site should derive line
 * value from here so the maths can't drift across components.
 */

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Effective unit count for an option. Defaults to 1 for legacy/blank
 * options and clamps out non-finite or sub-1 values (a line is always at
 * least one unit).
 */
export function optionQty(opt: Pick<QuoteOption, 'quantity'>): number {
  const q = opt.quantity;
  if (typeof q !== 'number' || !Number.isFinite(q) || q < 1) return 1;
  return Math.floor(q);
}

/**
 * Customer-facing ex-GST line total for an option: `quantity × sellPrice`,
 * rounded to 2dp. Guards a non-finite sellPrice to 0.
 */
export function optionLineSellExGst(
  opt: Pick<QuoteOption, 'quantity' | 'sellPrice'>,
): number {
  const unit = Number.isFinite(opt.sellPrice) ? opt.sellPrice : 0;
  return round2(optionQty(opt) * unit);
}

/**
 * Customer-facing line total for an option in the requested GST display
 * mode, rounded to mirror the invoice EXACTLY.
 *
 * The backend bills each option as `round(sellPrice * 1.1, 2)` per unit,
 * then × quantity (see `_build_legacy_orchestrator_lines`). So the inc-GST
 * line total must round the PER-UNIT price once and then multiply by qty.
 * Grossing up the rounded ex line total instead — `round2(round2(qty ×
 * unit) × 1.1)` — double-rounds and drifts a cent for ~9% of inc-GST
 * prices (e.g. a 0.05-inc unit reads back as 0.06). Ex mode keeps the plain
 * `quantity × unit` total.
 */
export function optionLineSellDisplay(
  opt: Pick<QuoteOption, 'quantity' | 'sellPrice'>,
  mode: 'ex' | 'inc',
): number {
  if (mode !== 'inc') return optionLineSellExGst(opt);
  const unit = Number.isFinite(opt.sellPrice) ? opt.sellPrice : 0;
  return round2(round2(unit * 1.1) * optionQty(opt));
}
